import { useEffect } from "react";
import { analytics } from "@/lib/analytics";

/**
 * Fires `onboarding_started` exactly ONCE per app session, at the entry to the
 * onboarding flow, regardless of which screen the user lands on first.
 *
 * Why a hook with a module-level guard (not a per-component ref): the onboarding
 * flow has no single container — Identity and Avatar are separate routes. The
 * usual path is Identity → Avatar, but several paths skip Identity and land
 * directly on Avatar (email/OAuth signup variations, "logged-in without avatar"
 * Mirror CTA, skip/back, deep-links). The event used to be wired ONLY to the
 * Identity mount, so every Identity-skipping path went uncounted (PostHog
 * 15/06: ~6 `onboarding_started` vs ~34 `avatar_created`). Instrumenting both
 * entry screens with a shared module-scope guard fires the event once for the
 * whole journey — a per-component ref would let the second screen re-fire.
 *
 * The guard resets on a full app reload (= a new onboarding session), and the
 * dedupe also prevents re-firing when navigating between sub-screens or back.
 */
let onboardingStartedFired = false;

export function useOnboardingStarted(
  path: "identity" | "avatar",
  enabled: boolean = true,
): void {
  useEffect(() => {
    // `enabled=false` (e.g. OnboardingAvatar reached as a Settings avatar EDIT)
    // skips WITHOUT consuming the guard, so a later genuine onboarding in the
    // same session still fires.
    if (!enabled) return;
    if (onboardingStartedFired) return;
    onboardingStartedFired = true;
    analytics.onboardingStarted(path);
  }, [path, enabled]);
}
